/**
 * MarbleFactory — vintage glass marbles (卷五 §2).
 * Each marble is physically imperfect: ±2% scale, random rotation phase,
 * iridescent "cat-eye" sheen, cold rim light colliding with the warm room,
 * and a translucent inner glow approximating subsurface scattering.
 */
import * as THREE from 'three';
import { MARBLE_RADIUS } from '@shared/constants/board';
import type { HexPos } from '@shared/types/game';

const _tmpColor = new THREE.Color('#E8F0FF');
const COLD_RIM_GLSL = `vec3(${_tmpColor.r}, ${_tmpColor.g}, ${_tmpColor.b})`;
/** Main light direction (must match GameEngine key light). */
const LIGHT_DIR = new THREE.Vector3(-0.42, 0.78, 0.46).normalize();
const LIGHT_DIR_GLSL = `vec3(${LIGHT_DIR.x}, ${LIGHT_DIR.y}, ${LIGHT_DIR.z})`;

export type MarbleVisualState = 'idle' | 'hover' | 'selected' | 'ghost';

export interface MarbleMesh {
  mesh: THREE.Mesh;
  key: string;
  seat: number;
  pos: HexPos;
  setEmissive: (intensity: number) => void;
  setColor: (hex: string) => void;
  material: THREE.MeshPhysicalMaterial;
}

let marbleGeo: THREE.BufferGeometry | null = null;
function getGeometry(): THREE.BufferGeometry {
  if (!marbleGeo) {
    marbleGeo = new THREE.SphereGeometry(MARBLE_RADIUS, 48, 32);
  }
  return marbleGeo;
}

/** Inject cold fresnel rim + fake-SSS glow into MeshPhysicalMaterial. */
function glassify(mat: THREE.MeshPhysicalMaterial): void {
  mat.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      /* glsl */ `
        #include <emissivemap_fragment>
        {
          vec3 vDir = normalize(vViewPosition);
          float fres = pow(1.0 - saturate(dot(vDir, normalize(normal))), 3.0);
          // 冷暖对撞：暖房间里玻璃边缘的冷白全反射
          totalEmissiveRadiance += ${COLD_RIM_GLSL} * fres * 0.42;
          // 背光透射（次表面散射近似）
          float sss = pow(saturate(dot(vDir, ${LIGHT_DIR_GLSL} * -1.0) * -1.0), 2.0);
          totalEmissiveRadiance += diffuseColor.rgb * sss * 0.55;
        }
        `
    );
  };
  mat.customProgramCacheKey = () => 'glass-marble';
}

export interface MarbleSpawn {
  seat: number;
  pos: HexPos;
  key: string;
  color: string;
  iridescence: number;
}

export function createMarble(spawn: MarbleSpawn): MarbleMesh {
  const mat = new THREE.MeshPhysicalMaterial({
    color: new THREE.Color(spawn.color),
    roughness: 0.07,
    metalness: 0,
    clearcoat: 1.0,
    clearcoatRoughness: 0.08,
    iridescence: spawn.iridescence,
    iridescenceIOR: 1.45,
    iridescenceThicknessRange: [140, 480],
    envMapIntensity: 1.1,
    emissive: new THREE.Color(spawn.color),
    emissiveIntensity: 0,
  });
  glassify(mat);

  const mesh = new THREE.Mesh(getGeometry(), mat);
  mesh.castShadow = true;
  mesh.receiveShadow = false;
  // 质感律：禁完美 — 尺度与旋转相位随机
  mesh.scale.setScalar(0.98 + Math.random() * 0.04);
  mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
  mesh.userData = { kind: 'marble', key: spawn.key, seat: spawn.seat };

  return {
    mesh,
    key: spawn.key,
    seat: spawn.seat,
    pos: spawn.pos,
    material: mat,
    setEmissive: (i: number) => {
      mat.emissiveIntensity = i;
    },
    setColor: (hex: string) => {
      mat.color.set(hex);
      mat.emissive.set(hex);
    },
  };
}

/** Shared ghost-ring marker for legal destinations. */
export function createTargetMarker(color: string, isHop: boolean): THREE.Mesh {
  const geo = new THREE.RingGeometry(0.2, isHop ? 0.34 : 0.28, 32);
  geo.rotateX(-Math.PI / 2);
  const mat = new THREE.MeshBasicMaterial({
    color: new THREE.Color(color),
    transparent: true,
    opacity: isHop ? 0.85 : 0.6,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 5;
  mesh.userData = { kind: 'marker' };
  return mesh;
}
